/**
 * UranIA PA — AI Disclosure banner (US-011 AC#2 + AC#3, esteso US-028 AC#3).
 *
 * Banner sticky che soddisfa l'obbligo di disclosure UE AI Act art. 50
 * + L. 132/2025 (recepimento italiano). Reso in ogni pagina dell'app via
 * `__root.tsx` (US-011), in modo che la compliance sia *strutturale* e
 * non opt-in dei singoli percorsi (US-028 AC#1 "globally injected in
 * Layout").
 *
 * Comportamento (AC#2 letterale):
 *   - Sticky topbar (`position: sticky; top: 0; z-index 1080`): l'avviso
 *     resta visibile in fondo al viewport anche durante lo scroll fino a
 *     dismiss esplicito.
 *   - Dismiss non permanente: lo stato vive in `useAiDisclosureBannerStore`
 *     (in-memory, niente persist) — refresh / nuova sessione ⇒ banner
 *     riappare. La motivazione regolatoria è in `banner-store.ts`.
 *   - Click dismiss loggato come evento audit
 *     `AiDisclosureBannerDismissed` via `recordUiAuditEvent` (US-F1-11
 *     tabella `audit_events`). Il transport è fire-and-forget — vedi
 *     `audit-client.ts`.
 *   - **US-028 AC#3**: ogni click sull'area banner (body, link Normattiva
 *     o bottone dismiss) emette un evento audit dedicato
 *     `DisclosureBannerClicked{view, ts, user_id_hash}` per il backend di
 *     compliance AgID (P.8 disclosure trasparente). `view` è il
 *     pathname della route corrente; `user_id_hash` è SHA-256 del
 *     subject OIDC (pseudonimo one-way: vedi `user-id-hash.ts`). Il
 *     dismiss continua ad emettere anche `AiDisclosureBannerDismissed`
 *     perché i due eventi descrivono fatti diversi (engagement vs.
 *     acknowledgement esplicito).
 *
 * Testo (AC#3 originale + US-028 AC#4):
 *   - i18n key `ai_disclosure.banner_long` (italiano primario `it-IT`,
 *     inglese fallback `en-EU`). Le due copy sono giuridicamente
 *     equivalenti: citano AI Act UE 2024/1689 art. 50 e L. 132/2025.
 *   - Aderente al meaning del legacy `app/snippets/pa_workspace_banner.py`
 *     citato in CLAUDE.md §"AI Act Art. 50 disclosure banner".
 *
 * A11y:
 *   - `role="region"` con `aria-label` esplicito (region-named landmark).
 *   - `role="alert"` evitato per non spammare lo screen reader ad ogni
 *     navigazione client-side; il `role="region"` con `aria-live="polite"`
 *     è la scelta WCAG 2.1 §1.3.1 conforme.
 *   - Bottone dismiss con `aria-label` (icona × decorativa, hidden agli SR).
 *   - Contrasto testo/sfondo ≥ 4.5:1 (palette AgID design.italia.it
 *     `--bs-warning` su nero — verificato da axe-core WCAG2 AA).
 *   - `onClick` sull'`<aside>` non altera il focus order: il banner
 *     resta un landmark non-interattivo (niente `tabIndex`, niente
 *     `role="button"`), il click è puro telemetry side-effect.
 */

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { JSX } from 'react';

import { recordUiAuditEvent } from '../../lib/audit/audit-client.ts';
import { computeUserIdHash } from '../../lib/audit/user-id-hash.ts';
import {
  selectAiDisclosureDismissed,
  useAiDisclosureBannerStore,
} from '../../lib/ai-disclosure/banner-store.ts';
import { selectPrincipal, useAuthStore } from '../../lib/auth/auth-store.ts';
import type { UraniaPrincipal } from '../../lib/auth/auth-store.ts';

function actorFromPrincipal(principal: UraniaPrincipal | null): {
  readonly subject: string | null;
  readonly tenantId: string | null;
  readonly roles: readonly string[];
} {
  return {
    subject: principal?.subject ?? null,
    tenantId: principal?.tenantId ?? null,
    roles: principal?.roles ?? [],
  };
}

function currentView(): string {
  return typeof window === 'undefined' ? '' : window.location.pathname;
}

export function AiDisclosureBanner(): JSX.Element | null {
  const { t } = useTranslation();
  const dismissed = useAiDisclosureBannerStore(selectAiDisclosureDismissed);
  const dismiss = useAiDisclosureBannerStore((s) => s.dismiss);
  const principal = useAuthStore(selectPrincipal);

  const emitBannerClicked = useCallback(async (): Promise<void> => {
    const userIdHash = await computeUserIdHash(principal?.subject ?? null);
    void recordUiAuditEvent({
      action: 'DisclosureBannerClicked',
      actor: actorFromPrincipal(principal),
      ts: new Date().toISOString(),
      attrs: {
        view: currentView(),
        user_id_hash: userIdHash,
        locale: typeof document === 'undefined' ? '' : document.documentElement.lang,
      },
    });
  }, [principal]);

  const handleBannerClick = useCallback((): void => {
    void emitBannerClicked();
  }, [emitBannerClicked]);

  const handleDismiss = useCallback((): void => {
    void recordUiAuditEvent({
      action: 'AiDisclosureBannerDismissed',
      actor: actorFromPrincipal(principal),
      ts: new Date().toISOString(),
      attrs: {
        path: currentView(),
        locale: typeof document === 'undefined' ? '' : document.documentElement.lang,
      },
    });
    dismiss();
  }, [dismiss, principal]);

  if (dismissed) {
    return null;
  }

  return (
    <aside
      className="urania-ai-disclosure rounded-0 m-0 py-2 px-3 small d-flex align-items-start gap-2"
      style={{
        backgroundColor: '#fff3cd',
        color: '#664d03',
        borderBottom: '1px solid #ffecb5',
      }}
      role="region"
      aria-label={t('ai_disclosure.region_label')}
      aria-live="polite"
      data-testid="ai-disclosure-banner"
      onClick={handleBannerClick}
    >
      <svg
        width="18"
        height="18"
        viewBox="0 0 16 16"
        fill="currentColor"
        className="flex-shrink-0 mt-1"
        aria-hidden="true"
      >
        <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z" />
      </svg>
      <div className="urania-ai-disclosure__body flex-grow-1">
        <strong>{t('ai_disclosure.heading')}</strong> <span>{t('ai_disclosure.banner_long')}</span>{' '}
        <a
          className="urania-ai-disclosure__link"
          href="https://www.normattiva.it/eli/id/2025/09/24/25G00138/sg"
          rel="noreferrer noopener external"
          target="_blank"
          data-testid="ai-disclosure-link-legge-132"
        >
          {t('ai_disclosure.link_legge_132')}
        </a>
      </div>
      <button
        type="button"
        className="btn-close urania-ai-disclosure__dismiss"
        aria-label={t('ai_disclosure.dismiss_aria')}
        data-testid="ai-disclosure-banner-dismiss"
        onClick={handleDismiss}
      />
    </aside>
  );
}
